/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CalendarPlus, Stethoscope, CalendarDays, Clock, UserRound, UserPlus, Search, Check, ChevronLeft, ChevronRight, X, DoorOpen, Mail, Phone } from 'lucide-react';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DATES_PER_PAGE = 7;

const fmtDateChip = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return {
    wd: d.toLocaleDateString('it-IT', { weekday: 'short' }).replace('.', ''),
    dm: d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
  };
};
const fmtDateLong = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';

function StepHeader({ n, icon: Icon, title, done, active }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className={`w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0 ${done ? 'bg-[#238A92] text-white' : active ? 'bg-[#238A92]/15 text-[#238A92] ring-2 ring-[#238A92]/30' : 'bg-[hsl(var(--secondary))] text-muted-foreground'}`}>
        {done ? <Check size={13} /> : n}
      </span>
      <Icon size={15} className={done || active ? 'text-[#238A92]' : 'text-muted-foreground'} />
      <p className={`text-sm font-semibold ${done || active ? 'text-[#1F2A37]' : 'text-muted-foreground'}`}>{title}</p>
    </div>
  );
}

export const NuovoAppuntamentoCard = ({ onCreated }) => {
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);

  const [doctorId, setDoctorId] = useState('');
  const [doctorQuery, setDoctorQuery] = useState('');
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [doctorIndex, setDoctorIndex] = useState(-1);
  const doctorBoxRef = useRef(null);
  const [serviceId, setServiceId] = useState('');

  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [datePage, setDatePage] = useState(0);
  const [date, setDate] = useState('');

  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);

  const [patientMode, setPatientMode] = useState('existing'); // existing | new
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [patient, setPatient] = useState(null);
  const [newPatient, setNewPatient] = useState({ nome: '', cognome: '', email: '', telefono: '' });

  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/doctors`)
      .then(r => setDoctors([...r.data].sort((a, b) => a.cognome.localeCompare(b.cognome, 'it'))))
      .catch(err => { logger.error('doctors', err); toast.error('Errore nel caricamento dei medici'); })
      .finally(() => setLoadingDoctors(false));
  }, []);

  const doctor = useMemo(() => doctors.find(d => d.id === doctorId), [doctors, doctorId]);

  // Ricerca medico per nome, cognome o specialità
  const normalize = (v) => (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const doctorMatches = useMemo(() => {
    const q = normalize(doctorQuery.trim());
    if (!q) return doctors.slice(0, 10);
    const words = q.split(/\s+/);
    return doctors.filter(d => {
      const hay = normalize(`${d.nome} ${d.cognome} ${d.specializzazione || ''}`);
      return words.every(w => hay.includes(w));
    }).slice(0, 10);
  }, [doctors, doctorQuery]);

  const pickDoctor = (d) => { setDoctorId(d.id); setDoctorQuery(`${d.nome} ${d.cognome}`); setDoctorOpen(false); setDoctorIndex(-1); };
  const clearDoctor = () => { setDoctorId(''); setDoctorQuery(''); setDoctorOpen(false); setDoctorIndex(-1); };
  const onDoctorKeyDown = (e) => {
    if (!doctorOpen) { if (e.key === 'ArrowDown') { setDoctorOpen(true); e.preventDefault(); } return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setDoctorIndex(i => (i + 1) % Math.max(doctorMatches.length, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setDoctorIndex(i => (i <= 0 ? doctorMatches.length - 1 : i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const d = doctorMatches[doctorIndex >= 0 ? doctorIndex : 0]; if (d) pickDoctor(d); }
    else if (e.key === 'Escape') { setDoctorOpen(false); setDoctorIndex(-1); }
  };
  useEffect(() => {
    const onDown = (e) => { if (doctorBoxRef.current && !doctorBoxRef.current.contains(e.target)) setDoctorOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);
  const hl = (text) => {
    const q = normalize(doctorQuery.trim());
    if (!q) return text;
    const words = q.split(/\s+/).filter(Boolean);
    const parts = text.split(new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig'));
    return parts.map((pt, i) => words.some(w => normalize(pt) === w) ? <mark key={i} className="bg-transparent text-[#238A92] font-semibold">{pt}</mark> : pt);
  };
  const services = useMemo(() => (doctor?.tariffe || []).map(t => ({ id: t.service_id, nome: t.nome_servizio, prezzo: t.prezzo_effettivo ?? t.prezzo })), [doctor]);

  // Cambio medico: reset a cascata
  useEffect(() => {
    setServiceId(services.length === 1 ? services[0].id : '');
    setDates([]); setDate(''); setSlots([]); setSlot(null); setDatePage(0);
    if (!doctorId) return;
    setDatesLoading(true);
    axios.get(`${API}/availability/dates`, { params: { doctor_id: doctorId } })
      .then(r => setDates(r.data?.dates || []))
      .catch(() => setDates([]))
      .finally(() => setDatesLoading(false));
  }, [doctorId, services]);

  // Cambio data: carica slot
  useEffect(() => {
    setSlots([]); setSlot(null);
    if (!doctorId || !date) return;
    setSlotsLoading(true);
    axios.get(`${API}/availability`, { params: { doctor_id: doctorId, data: date, service_id: serviceId || undefined } })
      .then(r => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [doctorId, date, serviceId]);

  // Ricerca paziente (debounce)
  useEffect(() => {
    if (patientMode !== 'existing') return;
    const q = patientSearch.trim();
    if (q.length < 2) { setPatientResults([]); return; }
    const t = setTimeout(() => {
      setPatientSearching(true);
      axios.get(`${API}/patients`, { params: { search: q, role: 'patient' } })
        .then(r => setPatientResults(r.data.slice(0, 8)))
        .catch(() => setPatientResults([]))
        .finally(() => setPatientSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch, patientMode]);

  const pagedDates = dates.slice(datePage * DATES_PER_PAGE, (datePage + 1) * DATES_PER_PAGE);
  const totalPages = Math.ceil(dates.length / DATES_PER_PAGE);

  const newPatientValid = newPatient.nome.trim() && newPatient.cognome.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newPatient.email.trim());
  const patientReady = patientMode === 'existing' ? !!patient : !!newPatientValid;
  const canSubmit = doctorId && serviceId && date && slot && patientReady && !saving;

  const reset = () => {
    setDoctorId(''); setDoctorQuery(''); setDoctorOpen(false); setDoctorIndex(-1); setServiceId(''); setDate(''); setSlot(null); setPatient(null);
    setPatientSearch(''); setPatientResults([]); setPatientMode('existing');
    setNewPatient({ nome: '', cognome: '', email: '', telefono: '' }); setNote('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      let patientId = patient?.id;
      let createdInfo = null;
      if (patientMode === 'new') {
        const res = await axios.post(`${API}/patients/quick-create`, {
          nome: newPatient.nome.trim(), cognome: newPatient.cognome.trim(),
          email: newPatient.email.trim(), telefono: newPatient.telefono.trim() || null,
        });
        patientId = res.data.user.id;
        createdInfo = res.data;
      }
      const res = await axios.post(`${API}/appointments`, {
        doctor_id: doctorId, service_id: serviceId, data: date,
        availability_id: slot.availability_id, ora: slot.ora,
        patient_id: patientId, note_paziente: note.trim() || null,
      });
      const a = res.data;
      toast.success(`Appuntamento creato: ${a.service_nome} con Dott. ${a.doctor_cognome}, ${fmtDateLong(a.data)} ore ${a.ora}`);
      if (createdInfo) {
        toast.info(createdInfo.email_sent
          ? `Nuovo paziente ${createdInfo.user.nome} ${createdInfo.user.cognome} registrato: gli è stata inviata l'email per impostare la password.`
          : `Nuovo paziente ${createdInfo.user.nome} ${createdInfo.user.cognome} registrato (email non configurata: usa "Reset password" dalla scheda paziente).`);
      }
      reset();
      onCreated?.(a);
    } catch (err) {
      logger.error('create appointment', err);
      toast.error(err.response?.data?.detail || 'Errore nella creazione dell\'appuntamento');
      if (err.response?.status === 409 && doctorId && date) {
        // slot preso nel frattempo: ricarica
        axios.get(`${API}/availability`, { params: { doctor_id: doctorId, data: date } }).then(r => { setSlots(r.data?.slots || []); setSlot(null); }).catch(() => {});
      }
    } finally { setSaving(false); }
  };

  return (
    <Card className="h-full" data-testid="new-appointment-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}>
            <CalendarPlus size={18} className="text-[#238A92]" /> Nuovo Appuntamento
          </CardTitle>
          {(doctorId || patient || patientMode === 'new') && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={reset} data-testid="new-appointment-reset"><X size={13} className="mr-1" /> Azzera</Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Inserimento manuale: medico, disponibilità e paziente (anche nuovo).</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* 1. Medico e servizio */}
        <section>
          <StepHeader n={1} icon={Stethoscope} title="Medico e servizio" done={!!doctorId && !!serviceId} active={!doctorId} />
          <div className="grid sm:grid-cols-2 gap-2 pl-8">
            {loadingDoctors ? <Skeleton className="h-10 rounded-md" /> : (
              <div className="relative" ref={doctorBoxRef}>
                <Stethoscope size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  value={doctorQuery}
                  onChange={e => { setDoctorQuery(e.target.value); setDoctorOpen(true); setDoctorIndex(-1); if (doctorId) setDoctorId(''); }}
                  onFocus={() => setDoctorOpen(true)}
                  onKeyDown={onDoctorKeyDown}
                  placeholder="Scrivi nome, cognome o specialità del medico..."
                  className={`pl-8 pr-8 ${doctorId ? 'border-[#238A92]/60 bg-[#238A92]/5 font-medium' : ''}`}
                  role="combobox"
                  aria-expanded={doctorOpen}
                  aria-controls="new-appointment-doctor-list"
                  aria-autocomplete="list"
                  autoComplete="off"
                  data-testid="new-appointment-doctor"
                />
                {doctorQuery && (
                  <button type="button" onClick={clearDoctor} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37]" aria-label="Pulisci medico" data-testid="new-appointment-doctor-clear"><X size={14} /></button>
                )}
                {doctorOpen && !doctorId && (
                  <div id="new-appointment-doctor-list" role="listbox" className="absolute z-30 left-0 right-0 top-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-md)] overflow-hidden" data-testid="new-appointment-doctor-list">
                    {doctorMatches.length === 0 ? (
                      <p className="px-3 py-2.5 text-xs text-muted-foreground">Nessun medico corrisponde a "{doctorQuery.trim()}".</p>
                    ) : (
                      <ul className="max-h-64 overflow-y-auto py-1">
                        {!doctorQuery.trim() && <li className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Inizia a scrivere per filtrare · {doctors.length} medici</li>}
                        {doctorMatches.map((d, i) => (
                          <li key={d.id} role="option" aria-selected={i === doctorIndex}>
                            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickDoctor(d)} onMouseEnter={() => setDoctorIndex(i)} className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${i === doctorIndex ? 'bg-[#238A92]/10' : 'hover:bg-[hsl(var(--secondary))]'}`} data-testid={`new-appointment-doctor-option-${d.id}`}>
                              <span className="truncate text-[#1F2A37]">Dott. {hl(`${d.nome} ${d.cognome}`)}</span>
                              <span className="text-xs text-muted-foreground truncate flex-shrink-0 max-w-[45%]">{hl(d.specializzazione || '')}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
            <Select value={serviceId} onValueChange={setServiceId} disabled={!doctorId || services.length === 0}>
              <SelectTrigger data-testid="new-appointment-service"><SelectValue placeholder={!doctorId ? 'Prima il medico' : services.length === 0 ? 'Nessun servizio associato' : 'Seleziona servizio...'} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {services.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}{s.prezzo != null ? <span className="text-muted-foreground"> · {Number(s.prezzo).toFixed(0)} €</span> : null}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* 2. Data e orario */}
        <section>
          <StepHeader n={2} icon={CalendarDays} title="Data e orario" done={!!slot} active={!!doctorId && !slot} />
          <div className="pl-8 space-y-2">
            {!doctorId ? (
              <p className="text-xs text-muted-foreground">Seleziona un medico per vedere le disponibilità.</p>
            ) : datesLoading ? (
              <div className="flex gap-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-14 rounded-lg" />)}</div>
            ) : dates.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="new-appointment-no-dates">Questo medico non ha disponibilità prenotabili. Generale dalla pagina Disponibilità.</p>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" disabled={datePage === 0} onClick={() => setDatePage(p => p - 1)} aria-label="Date precedenti"><ChevronLeft size={16} /></Button>
                  <div className="flex gap-1.5 flex-1 overflow-x-auto" data-testid="new-appointment-dates">
                    {pagedDates.map(d => {
                      const c = fmtDateChip(d);
                      const sel = d === date;
                      return (
                        <button key={d} type="button" onClick={() => setDate(d)} className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg border text-xs min-w-[56px] transition-colors ${sel ? 'bg-[#238A92] text-white border-[#238A92]' : 'bg-white text-[#1F2A37] border-[hsl(var(--border))] hover:border-[#238A92]/60'}`} data-testid={`new-appointment-date-${d}`}>
                          <span className={`capitalize ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>{c.wd}</span>
                          <span className="font-semibold">{c.dm}</span>
                        </button>
                      );
                    })}
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" disabled={datePage >= totalPages - 1} onClick={() => setDatePage(p => p + 1)} aria-label="Date successive"><ChevronRight size={16} /></Button>
                </div>
                {date && (
                  slotsLoading ? (
                    <div className="flex gap-1.5 flex-wrap">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-8 w-16 rounded-md" />)}</div>
                  ) : slots.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nessun orario libero in questa data.</p>
                  ) : (
                    <div className="flex gap-1.5 flex-wrap" data-testid="new-appointment-slots">
                      {slots.map(s => {
                        const sel = slot?.availability_id === s.availability_id;
                        return (
                          <button key={s.availability_id} type="button" onClick={() => setSlot(s)} title={s.studio_nome ? `Studio ${s.studio_numero || ''} ${s.studio_nome}` : ''} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${sel ? 'bg-[#238A92] text-white border-[#238A92]' : 'bg-white text-[#1F2A37] border-[hsl(var(--border))] hover:border-[#238A92]/60'}`} data-testid={`new-appointment-slot-${s.ora}`}>
                            <Clock size={11} /> {s.ora}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
                {slot && (
                  <p className="text-xs text-[#1F2A37] inline-flex items-center gap-2 flex-wrap" data-testid="new-appointment-slot-summary">
                    <Badge className="bg-[#238A92]/10 text-[#238A92] hover:bg-[#238A92]/10 capitalize">{fmtDateLong(date)} · ore {slot.ora}</Badge>
                    {slot.studio_nome && <span className="text-muted-foreground inline-flex items-center gap-1"><DoorOpen size={12} /> {slot.studio_numero ? `Studio ${slot.studio_numero} · ` : ''}{slot.studio_nome}</span>}
                    <span className="text-muted-foreground">{slot.durata_minuti} min</span>
                  </p>
                )}
              </>
            )}
          </div>
        </section>

        {/* 3. Paziente */}
        <section>
          <StepHeader n={3} icon={UserRound} title="Paziente" done={patientReady} active={!!slot && !patientReady} />
          <div className="pl-8 space-y-2">
            <div className="inline-flex rounded-lg border border-[hsl(var(--border))] p-0.5 bg-[hsl(var(--secondary))]">
              <button type="button" onClick={() => { setPatientMode('existing'); }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1 ${patientMode === 'existing' ? 'bg-white text-[#238A92] shadow-sm' : 'text-muted-foreground hover:text-[#1F2A37]'}`} data-testid="new-appointment-patient-existing"><Search size={12} /> Paziente registrato</button>
              <button type="button" onClick={() => { setPatientMode('new'); setPatient(null); }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1 ${patientMode === 'new' ? 'bg-white text-[#238A92] shadow-sm' : 'text-muted-foreground hover:text-[#1F2A37]'}`} data-testid="new-appointment-patient-new"><UserPlus size={12} /> Nuovo paziente</button>
            </div>

            {patientMode === 'existing' ? (
              patient ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-[#238A92]/40 bg-[#238A92]/5 px-3 py-2" data-testid="new-appointment-patient-selected">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1F2A37] truncate">{patient.nome} {patient.cognome}</p>
                    <p className="text-xs text-muted-foreground truncate">{patient.email}{patient.telefono ? ` · ${patient.telefono}` : ''}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs flex-shrink-0" onClick={() => { setPatient(null); setPatientSearch(''); }}>Cambia</Button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
                  <Input value={patientSearch} onChange={e => setPatientSearch(e.target.value)} placeholder="Cerca per nome, cognome o email (min. 2 lettere)..." className="pl-8" data-testid="new-appointment-patient-search" />
                  {patientSearch.trim().length >= 2 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-md)] overflow-hidden" data-testid="new-appointment-patient-results">
                      {patientSearching ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">Ricerca...</p>
                      ) : patientResults.length === 0 ? (
                        <div className="px-3 py-2.5 text-xs text-muted-foreground flex items-center justify-between gap-2">
                          <span>Nessun paziente trovato.</span>
                          <button type="button" className="text-[#238A92] font-medium hover:underline inline-flex items-center gap-1" onClick={() => { setPatientMode('new'); const parts = patientSearch.trim().split(/\s+/); setNewPatient(p => ({ ...p, nome: parts[0] || '', cognome: parts.slice(1).join(' ') })); }}><UserPlus size={12} /> Crea nuovo</button>
                        </div>
                      ) : patientResults.map(p => (
                        <button key={p.id} type="button" onClick={() => { setPatient(p); setPatientSearch(''); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--secondary))] transition-colors flex items-center justify-between gap-2" data-testid={`new-appointment-patient-result-${p.id}`}>
                          <span className="truncate"><span className="font-medium text-[#1F2A37]">{p.nome} {p.cognome}</span> <span className="text-xs text-muted-foreground">{p.email}</span></span>
                          {p.telefono && <span className="text-xs text-muted-foreground flex-shrink-0">{p.telefono}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="grid sm:grid-cols-2 gap-2 rounded-lg border border-dashed border-[#238A92]/40 p-3 bg-[#238A92]/[0.03]" data-testid="new-appointment-new-patient-form">
                <div className="space-y-1">
                  <Label htmlFor="np-nome" className="text-xs">Nome *</Label>
                  <Input id="np-nome" value={newPatient.nome} onChange={e => setNewPatient(p => ({ ...p, nome: e.target.value }))} className="h-9" data-testid="new-appointment-np-nome" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="np-cognome" className="text-xs">Cognome *</Label>
                  <Input id="np-cognome" value={newPatient.cognome} onChange={e => setNewPatient(p => ({ ...p, cognome: e.target.value }))} className="h-9" data-testid="new-appointment-np-cognome" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="np-email" className="text-xs inline-flex items-center gap-1"><Mail size={11} /> Email *</Label>
                  <Input id="np-email" type="email" value={newPatient.email} onChange={e => setNewPatient(p => ({ ...p, email: e.target.value }))} className="h-9" placeholder="nome@esempio.com" data-testid="new-appointment-np-email" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="np-tel" className="text-xs inline-flex items-center gap-1"><Phone size={11} /> Telefono</Label>
                  <Input id="np-tel" type="tel" value={newPatient.telefono} onChange={e => setNewPatient(p => ({ ...p, telefono: e.target.value }))} className="h-9" placeholder="+39 333 1234567" data-testid="new-appointment-np-telefono" />
                </div>
                <p className="sm:col-span-2 text-[11px] text-muted-foreground">Il paziente riceverà via email il link per impostare la password e accedere alla sua area. Il telefono serve per le comunicazioni WhatsApp.</p>
              </div>
            )}
          </div>
        </section>

        {/* Note + submit */}
        <div className="pl-8 space-y-3">
          <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Note per lo staff/medico (opzionale)" className="text-sm" data-testid="new-appointment-note" />
          <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="new-appointment-submit">
            <CalendarPlus size={16} className="mr-1.5" /> {saving ? 'Creazione in corso...' : 'Crea appuntamento'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
